export interface AdjustmentValues {
  brightness: number;
  contrast: number;
  saturation: number;
  warmth: number;
  sharpness: number;
}

export const DEFAULT_ADJUSTMENTS: AdjustmentValues = {
  brightness: 0,
  contrast: 0,
  saturation: 0,
  warmth: 0,
  sharpness: 0,
};

export interface FilterPreset {
  id: string;
  name: string;
  adjustments: AdjustmentValues;
}

export const CAR_FILTER_PRESETS: FilterPreset[] = [
  {
    id: "showroom",
    name: "Showroom",
    adjustments: { brightness: 12, contrast: 18, saturation: 10, warmth: 8, sharpness: 5 },
  },
  {
    id: "golden-hour",
    name: "Golden Hour",
    adjustments: { brightness: 8, contrast: -5, saturation: 5, warmth: 30, sharpness: 0 },
  },
  {
    id: "midnight-luxe",
    name: "Midnight Luxe",
    adjustments: { brightness: -10, contrast: 15, saturation: -5, warmth: -20, sharpness: 5 },
  },
  {
    id: "heritage",
    name: "Heritage",
    adjustments: { brightness: 5, contrast: -3, saturation: -25, warmth: 18, sharpness: 0 },
  },
  {
    id: "concours",
    name: "Concours",
    adjustments: { brightness: 3, contrast: 10, saturation: 8, warmth: 0, sharpness: 15 },
  },
  {
    id: "british-racing",
    name: "British Racing",
    adjustments: { brightness: 0, contrast: 12, saturation: 15, warmth: 5, sharpness: 8 },
  },
];

function clamp(val: number, min: number, max: number): number {
  return Math.max(min, Math.min(max, val));
}

export function applyAdjustments(
  sourceCanvas: HTMLCanvasElement,
  adjustments: AdjustmentValues
): HTMLCanvasElement {
  const w = sourceCanvas.width;
  const h = sourceCanvas.height;
  const outCanvas = document.createElement("canvas");
  outCanvas.width = w;
  outCanvas.height = h;
  const ctx = outCanvas.getContext("2d")!;

  ctx.drawImage(sourceCanvas, 0, 0);
  const imageData = ctx.getImageData(0, 0, w, h);
  const d = imageData.data;

  const br = adjustments.brightness / 100;
  const co = adjustments.contrast / 100;
  const sa = adjustments.saturation / 100;
  const wa = adjustments.warmth / 100;

  // Bug fix (inbox feedback, May 2026 — "the auto filter does
  // something with the photo"): the contrast formula used to be
  //     contrastFactor = (1 + co) / (1 - co * 0.5 + 0.5)
  // which at the slider's neutral position (`co === 0`) evaluates
  // to `1 / 1.5 ≈ 0.667`, NOT 1.0. Every channel was then run
  // through `factor * (v - 128) + 128`, so even with every
  // adjustment slider at 0 the editor crushed the image toward
  // middle-grey by ~33% — bright skies dropped from 255→213,
  // deep shadows lifted from 0→43, the whole frame went flat and
  // dull. Hosts reported this as a "the editor darkens my photo"
  // bug. The stray `+ 0.5` in the denominator was a typo (the
  // intended denominator was `1 - co * 0.5`, equivalent to
  // `(1 - co) * 0.5 + 0.5`). With it removed:
  //   - co =  0    → factor = 1.0    (no-op, as expected)
  //   - co = +0.5  → factor = 2.0    (slider max boosts contrast)
  //   - co = -0.5  → factor = 0.4    (slider min flattens contrast)
  const contrastFactor = (1 + co) / (1 - co * 0.5);

  for (let i = 0; i < d.length; i += 4) {
    let r = d[i];
    let g = d[i + 1];
    let b = d[i + 2];

    r += r * br;
    g += g * br;
    b += b * br;

    r = contrastFactor * (r - 128) + 128;
    g = contrastFactor * (g - 128) + 128;
    b = contrastFactor * (b - 128) + 128;

    const gray = 0.2126 * r + 0.7152 * g + 0.0722 * b;
    r = gray + (r - gray) * (1 + sa);
    g = gray + (g - gray) * (1 + sa);
    b = gray + (b - gray) * (1 + sa);

    r += wa * 15;
    g += wa * 5;
    b -= wa * 10;

    d[i] = clamp(Math.round(r), 0, 255);
    d[i + 1] = clamp(Math.round(g), 0, 255);
    d[i + 2] = clamp(Math.round(b), 0, 255);
  }

  ctx.putImageData(imageData, 0, 0);

  if (adjustments.sharpness > 0) {
    applySharpen(ctx, w, h, adjustments.sharpness / 100);
  }

  return outCanvas;
}

function applySharpen(
  ctx: CanvasRenderingContext2D,
  w: number,
  h: number,
  amount: number
): void {
  const imageData = ctx.getImageData(0, 0, w, h);
  const src = new Uint8ClampedArray(imageData.data);
  const dst = imageData.data;
  // Strength multiplier bumped from 0.5 → 1.5 so the slider produces a
  // visible effect. At slider max (30) `amount` is 0.3, so the prior
  // 0.15 effective strength was below the just-noticeable threshold for
  // most photos. 0.45 at max gives a clear, pleasing edge sharpening
  // without ringing artefacts on typical car listing imagery.
  const strength = amount * 1.5;

  for (let y = 1; y < h - 1; y++) {
    for (let x = 1; x < w - 1; x++) {
      const idx = (y * w + x) * 4;
      for (let c = 0; c < 3; c++) {
        const center = src[idx + c];
        const neighbors =
          src[((y - 1) * w + x) * 4 + c] +
          src[((y + 1) * w + x) * 4 + c] +
          src[(y * w + x - 1) * 4 + c] +
          src[(y * w + x + 1) * 4 + c];
        const sharp = center + (center * 4 - neighbors) * strength;
        dst[idx + c] = clamp(Math.round(sharp), 0, 255);
      }
    }
  }

  ctx.putImageData(imageData, 0, 0);
}

export function imageToCanvas(img: HTMLImageElement): HTMLCanvasElement {
  const canvas = document.createElement("canvas");
  canvas.width = img.naturalWidth;
  canvas.height = img.naturalHeight;
  const ctx = canvas.getContext("2d")!;
  // Audit fix (May 2026 photo pipeline audit): every downstream
  // export from this canvas is JPEG (toDataURL("image/jpeg") /
  // toBlob("image/jpeg")). JPEG has no alpha channel, so the
  // browser fills transparent source pixels with BLACK on encode.
  // For PNGs with transparency (including iPhone-exported HEIC→PNG
  // and any logo overlay), that turned cropped corners and any
  // transparent regions into the "black bands / black corners"
  // hosts have reported in the editor and saved thumbnails.
  // Filling the canvas with white BEFORE drawImage gives JPEG
  // encoders a non-transparent surface to compress, matching the
  // behaviour every other photo viewer assumes for these images.
  ctx.fillStyle = "#ffffff";
  ctx.fillRect(0, 0, canvas.width, canvas.height);
  ctx.drawImage(img, 0, 0);
  return canvas;
}

export function canvasToBlob(canvas: HTMLCanvasElement, quality = 0.9): Promise<Blob> {
  return new Promise((resolve, reject) => {
    canvas.toBlob(
      (blob) => (blob ? resolve(blob) : reject(new Error("Blob creation failed"))),
      "image/jpeg",
      quality
    );
  });
}

export function generateFilterThumbnail(
  sourceCanvas: HTMLCanvasElement,
  adjustments: AdjustmentValues,
  size = 80
): string {
  const thumbCanvas = document.createElement("canvas");
  thumbCanvas.width = size;
  thumbCanvas.height = size;
  const ctx = thumbCanvas.getContext("2d")!;

  const aspect = sourceCanvas.width / sourceCanvas.height;
  let sw = size, sh = size;
  if (aspect > 1) {
    sh = size / aspect;
  } else {
    sw = size * aspect;
  }
  const sx = (size - sw) / 2;
  const sy = (size - sh) / 2;

  // Audit fix: pre-fill white so JPEG export of the letterboxed
  // thumbnail doesn't produce black bars at the top/bottom (or
  // sides) when the source has a non-1:1 aspect ratio. Without
  // this, the thumbnails in the filter strip had thin black bars.
  ctx.fillStyle = "#ffffff";
  ctx.fillRect(0, 0, size, size);
  ctx.drawImage(sourceCanvas, sx, sy, sw, sh);

  const tempCanvas = document.createElement("canvas");
  tempCanvas.width = size;
  tempCanvas.height = size;
  const tempCtx = tempCanvas.getContext("2d")!;
  tempCtx.drawImage(thumbCanvas, 0, 0);

  const filtered = applyAdjustments(tempCanvas, adjustments);
  return filtered.toDataURL("image/jpeg", 0.6);
}
